import React, { useEffect, useState } from "react";
import { Box, Typography, keyframes } from "@mui/material";
import {
  Whatshot as HotSeatIcon,
  Groups as UsersIcon,
  HourglassTop as WaitingIcon,
  Construction as BuildingIcon,
  Bolt as ForceIcon,
  Campaign as AnnounceIcon,
  Celebration as CelebrationIcon,
  Casino as WheelIcon,
  Stars as StarsIcon,
  Flare as FlareIcon,
} from "@mui/icons-material";
import { SPIN, spinFadeUp, spinPulse, spinPanelSx } from "./spinTheme";
import DefaultStyleWheel from "../../Common/DefaultStyleWheel";
import { SW_GAME_TYPES, SW_PHASES } from "./spinHostPhases";

/* ─── shared keyframes ─── */
const countdownPop = keyframes`
  0% { transform: scale(0.3); opacity: 0; }
  50% { transform: scale(1.2); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
`;

const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-8px); }
`;

const glowPulse = keyframes`
  0%, 100% { box-shadow: 0 0 20px rgba(255,193,7,0.2), 0 0 40px rgba(255,193,7,0.1); }
  50% { box-shadow: 0 0 40px rgba(255,193,7,0.4), 0 0 80px rgba(255,193,7,0.2); }
`;

/* ─── shared shells ─── */
const StageShell = ({ accent, children, glow = false }) => (
  <Box
    sx={{
      flex: 1,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      px: 2,
      zIndex: 1,
      animation: `${spinFadeUp} 0.55s ease both`,
    }}
  >
    <Box
      sx={{
        ...spinPanelSx,
        maxWidth: 520,
        borderColor: `${accent}44`,
        boxShadow: `0 0 40px ${accent}18`,
        animation: glow ? `${glowPulse} 3s ease-in-out infinite` : "none",
      }}
    >
      {children}
    </Box>
  </Box>
);

const Eyebrow = ({ children, color }) => (
  <Typography
    sx={{
      color,
      fontWeight: 800,
      letterSpacing: "0.18em",
      fontSize: { xs: "0.68rem", sm: "0.75rem" },
      mb: 1.25,
      fontFamily: SPIN.fontDisplay,
      textTransform: "uppercase",
    }}
  >
    {children}
  </Typography>
);

const GradientTitle = ({ children, accent, secondary }) => (
  <Typography
    sx={{
      fontFamily: SPIN.fontDisplay,
      fontWeight: 800,
      fontSize: { xs: "1.55rem", sm: "1.9rem" },
      letterSpacing: "-0.02em",
      mb: 1.25,
      background: `linear-gradient(90deg, ${accent}, ${secondary || SPIN.gold})`,
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
    }}
  >
    {children}
  </Typography>
);

/* ─── WAITING ─── */
export const SpinHostWaitingView = () => (
  <StageShell accent={SPIN.gold}>
    <WaitingIcon sx={{ fontSize: 44, color: SPIN.gold, mb: 1.5, opacity: 0.9 }} />
    <Typography
      sx={{
        fontFamily: SPIN.fontDisplay,
        fontWeight: 700,
        mb: 1,
        fontSize: { xs: "1.2rem", sm: "1.35rem" },
      }}
    >
      Play with Host
    </Typography>
    <Typography sx={{ color: SPIN.muted, fontSize: "0.92rem", lineHeight: 1.55 }}>
      Waiting for the host to start Users Wheel or Hot Seat. This screen updates live when they do.
    </Typography>
  </StageShell>
);

/* ─── INTRO (Users Wheel — legacy) ─── */
export const SpinHostIntroView = ({ gameType }) => {
  const isHot = gameType === SW_GAME_TYPES.HOT_SEAT;
  const accent = isHot ? SPIN.hot : SPIN.live;
  const Icon = isHot ? HotSeatIcon : UsersIcon;

  return (
    <StageShell accent={accent}>
      <Box
        sx={{
          width: 72,
          height: 72,
          borderRadius: "50%",
          mx: "auto",
          mb: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: `${accent}22`,
          color: accent,
          animation: `${spinPulse} 2.2s ease-in-out infinite`,
        }}
      >
        <Icon sx={{ fontSize: 36 }} />
      </Box>
      <Eyebrow color={accent}>{isHot ? "HOT SEAT" : "USERS WHEEL"}</Eyebrow>
      <GradientTitle accent={accent}>
        {isHot ? "Welcome to the Hot Seat" : "Users Wheel is starting"}
      </GradientTitle>
      <Typography sx={{ color: SPIN.muted, fontSize: "0.95rem", lineHeight: 1.55 }}>
        {isHot
          ? "One player. One prize wheel. The host runs the show — stay locked in."
          : "The room is on the wheel. The host is about to spin for real prizes."}
      </Typography>
    </StageShell>
  );
};

/* ─── BTN 1 · INVITE — "Invite people to spin the wheel" ─── */
export const SpinHostInviteView = ({ announceWheel }) => (
  <StageShell accent={SPIN.gold} glow>
    <Box
      sx={{
        width: 80,
        height: 80,
        borderRadius: "50%",
        mx: "auto",
        mb: 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "rgba(255,193,7,0.15)",
        color: SPIN.gold,
        animation: `${float} 3s ease-in-out infinite`,
      }}
    >
      <CelebrationIcon sx={{ fontSize: 42 }} />
    </Box>
    <Eyebrow color={SPIN.gold}>SPIN THE WHEEL</Eyebrow>
    <GradientTitle accent={SPIN.gold} secondary={SPIN.hot}>
      Get Ready to Spin!
    </GradientTitle>
    <Typography
      sx={{
        color: "#fff",
        fontSize: { xs: "1rem", sm: "1.1rem" },
        lineHeight: 1.6,
        mb: 2,
        fontWeight: 500,
      }}
    >
      The host is about to start a round of Spin the Wheel.
      <br />
      Stay tuned — prizes are on the line!
    </Typography>

    <Box
      sx={{
        display: "flex",
        gap: 2,
        justifyContent: "center",
        flexWrap: "wrap",
        mt: 1,
      }}
    >
      <Box sx={{ textAlign: "center" }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "12px",
            bgcolor: "rgba(255,45,138,0.15)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mx: "auto",
            mb: 0.5,
          }}
        >
          <UsersIcon sx={{ color: SPIN.live, fontSize: 24 }} />
        </Box>
        <Typography sx={{ fontSize: "0.72rem", color: SPIN.muted, fontWeight: 600 }}>
          Users Wheel
        </Typography>
      </Box>
      <Box sx={{ textAlign: "center" }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "12px",
            bgcolor: "rgba(255,107,61,0.15)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mx: "auto",
            mb: 0.5,
          }}
        >
          <HotSeatIcon sx={{ color: SPIN.hot, fontSize: 24 }} />
        </Box>
        <Typography sx={{ fontSize: "0.72rem", color: SPIN.muted, fontWeight: 600 }}>
          Hot Seat
        </Typography>
      </Box>
      <Box sx={{ textAlign: "center" }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "12px",
            bgcolor: "rgba(255,193,7,0.15)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mx: "auto",
            mb: 0.5,
          }}
        >
          <WheelIcon sx={{ color: SPIN.gold, fontSize: 24 }} />
        </Box>
        <Typography sx={{ fontSize: "0.72rem", color: SPIN.muted, fontWeight: 600 }}>
          Prize Wheel
        </Typography>
      </Box>
    </Box>
  </StageShell>
);

/* ─── BTN 2 · ANNOUNCE — Show the wheel we're about to play ─── */
export const SpinHostAnnounceView = ({ announceWheel }) => {
  const wheel = announceWheel || {};
  const slices = wheel.slices || [];
  const sliceTotal = Number(wheel.sliceCount ?? slices.length) || 0;

  return (
    <StageShell accent={SPIN.hot} glow>
      <Box
        sx={{
          width: 72,
          height: 72,
          borderRadius: "50%",
          mx: "auto",
          mb: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "rgba(255,107,61,0.18)",
          color: SPIN.hot,
          animation: `${spinPulse} 2s ease-in-out infinite`,
        }}
      >
        <AnnounceIcon sx={{ fontSize: 38 }} />
      </Box>
      <Eyebrow color={SPIN.hot}>TONIGHT&apos;S WHEEL</Eyebrow>
      <GradientTitle accent={SPIN.hot} secondary={SPIN.gold}>
        {wheel.wheel_name || "Prize Wheel"}
      </GradientTitle>

      {wheel.description && (
        <Typography
          sx={{
            color: "#fff",
            fontSize: "0.95rem",
            lineHeight: 1.6,
            mb: 2,
            fontWeight: 500,
          }}
        >
          {wheel.description}
        </Typography>
      )}

      {sliceTotal > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography
            sx={{
              fontSize: "0.72rem",
              color: SPIN.muted,
              fontWeight: 700,
              letterSpacing: "0.1em",
              mb: 1.25,
            }}
          >
            {sliceTotal} {sliceTotal === 1 ? "SLICE" : "SLICES"} ON THE WHEEL
          </Typography>
          <DefaultStyleWheel
            slices={slices}
            logoUrl="/mind_your_music.png"
            maxSize={220}
          />
        </Box>
      )}
    </StageShell>
  );
};

/* ─── BTN 3 · HOT_SEAT_SLIDE — "You could be the lucky player…" ─── */
export const SpinHostHotSeatSlideView = () => (
  <StageShell accent={SPIN.hot} glow>
    <Box
      sx={{
        width: 88,
        height: 88,
        borderRadius: "50%",
        mx: "auto",
        mb: 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: `linear-gradient(135deg, ${SPIN.hot}33, ${SPIN.gold}22)`,
        color: SPIN.hot,
        animation: `${float} 2.5s ease-in-out infinite`,
      }}
    >
      <HotSeatIcon sx={{ fontSize: 48 }} />
    </Box>
    <Eyebrow color={SPIN.hot}>HOT SEAT</Eyebrow>
    <GradientTitle accent={SPIN.hot} secondary={SPIN.gold}>
      Who Will It Be?
    </GradientTitle>
    <Typography
      sx={{
        color: "#fff",
        fontSize: { xs: "1rem", sm: "1.15rem" },
        lineHeight: 1.7,
        fontWeight: 500,
        maxWidth: 380,
        mx: "auto",
      }}
    >
      You could be the lucky player to join the hot seat…
    </Typography>
    <Typography
      sx={{
        color: SPIN.gold,
        fontSize: { xs: "0.9rem", sm: "1rem" },
        lineHeight: 1.7,
        fontWeight: 700,
        mt: 1,
        fontFamily: SPIN.fontDisplay,
      }}
    >
      Keep your fingers crossed and watch the space!
    </Typography>
    <StarsIcon
      sx={{
        fontSize: 28,
        color: SPIN.gold,
        mt: 2,
        opacity: 0.6,
        animation: `${spinPulse} 1.5s ease-in-out infinite`,
      }}
    />
  </StageShell>
);

/* ─── BTN 4 · LUCKY_PLAYER — "Lucky player" slide ─── */
export const SpinHostLuckyPlayerView = () => (
  <StageShell accent={SPIN.live} glow>
    <Box
      sx={{
        width: 88,
        height: 88,
        borderRadius: "50%",
        mx: "auto",
        mb: 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: `linear-gradient(135deg, ${SPIN.live}33, ${SPIN.gold}22)`,
        color: SPIN.live,
        animation: `${float} 2s ease-in-out infinite`,
      }}
    >
      <FlareIcon sx={{ fontSize: 48 }} />
    </Box>
    <Eyebrow color={SPIN.live}>USERS WHEEL</Eyebrow>
    <GradientTitle accent={SPIN.live} secondary={SPIN.gold}>
      Lucky Player Selection
    </GradientTitle>
    <Typography
      sx={{
        color: "#fff",
        fontSize: { xs: "1rem", sm: "1.15rem" },
        lineHeight: 1.7,
        fontWeight: 500,
        maxWidth: 400,
        mx: "auto",
      }}
    >
      You could be the lucky player to join the hot seat…
    </Typography>
    <Typography
      sx={{
        color: SPIN.gold,
        fontSize: { xs: "0.9rem", sm: "1rem" },
        lineHeight: 1.7,
        fontWeight: 700,
        mt: 1,
        fontFamily: SPIN.fontDisplay,
      }}
    >
      Keep your fingers crossed and watch the space!
    </Typography>
    <UsersIcon
      sx={{
        fontSize: 28,
        color: SPIN.live,
        mt: 2,
        opacity: 0.6,
        animation: `${spinPulse} 1.5s ease-in-out infinite`,
      }}
    />
  </StageShell>
);

/* ─── BTN 5 · BUILDING_USER_WHEEL — "Building the user wheel" ─── */
export const SpinHostBuildingUserWheelView = () => (
  <StageShell accent={SPIN.live}>
    <BuildingIcon
      sx={{
        fontSize: 48,
        color: SPIN.live,
        mb: 1.5,
        animation: `${spinPulse} 1.6s ease-in-out infinite`,
      }}
    />
    <Eyebrow color={SPIN.live}>BUILDING</Eyebrow>
    <Typography
      sx={{
        fontFamily: SPIN.fontDisplay,
        fontWeight: 800,
        fontSize: { xs: "1.3rem", sm: "1.5rem" },
        mb: 1,
        background: `linear-gradient(90deg, ${SPIN.live}, ${SPIN.gold})`,
        backgroundSize: "200% auto",
        animation: `${shimmer} 3s linear infinite`,
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent",
      }}
    >
      Building The Users Wheel…
    </Typography>
    <Typography sx={{ color: SPIN.muted, fontSize: "0.92rem", lineHeight: 1.55 }}>
      The host is adding players to the wheel. Hang tight — the spin is coming soon!
    </Typography>
  </StageShell>
);

/* ─── BTN 6 · COUNTDOWN — 3…2…1… ─── */
export const SpinHostCountdownView = () => {
  const [count, setCount] = useState(3);

  useEffect(() => {
    if (count <= 0) return undefined;
    const t = setTimeout(() => setCount((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [count]);

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        zIndex: 1,
      }}
    >
      <Box
        sx={{
          textAlign: "center",
          animation: `${spinFadeUp} 0.3s ease both`,
        }}
      >
        {count > 0 ? (
          <Typography
            key={count}
            sx={{
              fontFamily: SPIN.fontDisplay,
              fontWeight: 900,
              fontSize: { xs: "6rem", sm: "9rem", md: "12rem" },
              lineHeight: 1,
              background: `linear-gradient(135deg, ${SPIN.gold}, ${SPIN.hot})`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              filter: "drop-shadow(0 0 40px rgba(255,193,7,0.4))",
              animation: `${countdownPop} 0.6s ease-out both`,
            }}
          >
            {count}
          </Typography>
        ) : (
          <Typography
            sx={{
              fontFamily: SPIN.fontDisplay,
              fontWeight: 900,
              fontSize: { xs: "2.5rem", sm: "3.5rem", md: "4.5rem" },
              lineHeight: 1,
              background: `linear-gradient(135deg, ${SPIN.live}, ${SPIN.gold}, ${SPIN.hot})`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              filter: "drop-shadow(0 0 40px rgba(255,45,138,0.4))",
              animation: `${countdownPop} 0.5s ease-out both`,
            }}
          >
            LET&apos;S GO!
          </Typography>
        )}
      </Box>
    </Box>
  );
};

/* ─── Legacy views (kept for backward compat with Users Wheel flow) ─── */

export const SpinHostGameAboutToStartView = ({ gameType }) => {
  const isHot = gameType === SW_GAME_TYPES.HOT_SEAT;
  const accent = isHot ? SPIN.hot : SPIN.live;

  return (
    <StageShell accent={accent}>
      <Typography
        sx={{
          fontFamily: SPIN.fontDisplay,
          fontWeight: 800,
          fontSize: { xs: "1.4rem", sm: "1.75rem" },
          letterSpacing: "0.04em",
          mb: 1,
          color: SPIN.gold,
          animation: `${spinPulse} 1.8s ease-in-out infinite`,
        }}
      >
        Game is about to start
      </Typography>
      <Typography sx={{ color: SPIN.muted, fontSize: "0.95rem", lineHeight: 1.55 }}>
        {isHot
          ? "Hot Seat is warming up. Eyes on the host."
          : "Users Wheel is warming up. Eyes on the host."}
      </Typography>
    </StageShell>
  );
};

export const SpinHostForceView = ({ gameType }) => {
  const isHot = gameType === SW_GAME_TYPES.HOT_SEAT;
  const accent = isHot ? SPIN.hot : SPIN.live;

  return (
    <StageShell accent={accent}>
      <ForceIcon sx={{ fontSize: 40, color: accent, mb: 1.5 }} />
      <Typography
        sx={{
          fontFamily: SPIN.fontDisplay,
          fontWeight: 800,
          fontSize: "1.25rem",
          mb: 1,
        }}
      >
        Host is locking the room
      </Typography>
      <Typography sx={{ color: SPIN.muted, fontSize: "0.92rem", lineHeight: 1.55 }}>
        Stay on this screen — the host is forcing the next stage.
      </Typography>
    </StageShell>
  );
};

export const SpinHostSelectHotSeatView = ({ hotSeatName }) => (
  <StageShell accent={SPIN.hot}>
    <HotSeatIcon sx={{ fontSize: 42, color: SPIN.hot, mb: 1.5 }} />
    <Eyebrow color={SPIN.hot}>SELECTING HOT SEAT</Eyebrow>
    <Typography
      sx={{
        fontFamily: SPIN.fontDisplay,
        fontWeight: 800,
        fontSize: "1.3rem",
        mb: 1,
      }}
    >
      {hotSeatName ? `${hotSeatName} is in the Hot Seat` : "Host is choosing the Hot Seat"}
    </Typography>
    <Typography sx={{ color: SPIN.muted, fontSize: "0.92rem", lineHeight: 1.55 }}>
      {hotSeatName
        ? "Prize wheel setup is next. Hang tight."
        : "Someone is about to take the chair."}
    </Typography>
  </StageShell>
);

export const SpinHostBuildingWheelView = ({ gameType }) => {
  const isHot = gameType === SW_GAME_TYPES.HOT_SEAT;
  const accent = isHot ? SPIN.hot : SPIN.live;

  return (
    <StageShell accent={accent}>
      <BuildingIcon
        sx={{
          fontSize: 44,
          color: accent,
          mb: 1.5,
          animation: `${spinPulse} 1.6s ease-in-out infinite`,
        }}
      />
      <Typography
        sx={{
          fontFamily: SPIN.fontDisplay,
          fontWeight: 800,
          fontSize: { xs: "1.25rem", sm: "1.45rem" },
          mb: 1,
        }}
      >
        Building The Wheel…
      </Typography>
      <Typography sx={{ color: SPIN.muted, fontSize: "0.92rem", lineHeight: 1.55 }}>
        Preparing your wheel. The host is configuring slices — it appears when they hit Start Wheel.
      </Typography>
    </StageShell>
  );
};

/* ─── ROUTER — map phase → view ─── */
export const renderSpinHostPhaseView = ({
  phase,
  gameType,
  hotSeatName,
  announceWheel,
}) => {
  if (!phase || phase === SW_PHASES.WAITING) {
    return <SpinHostWaitingView />;
  }
  // Enhanced Hot Seat flow phases
  if (phase === SW_PHASES.INVITE) {
    return <SpinHostInviteView announceWheel={announceWheel} />;
  }
  if (phase === SW_PHASES.ANNOUNCE) {
    return <SpinHostAnnounceView announceWheel={announceWheel} />;
  }
  if (phase === SW_PHASES.HOT_SEAT_SLIDE) {
    return <SpinHostHotSeatSlideView />;
  }
  if (phase === SW_PHASES.LUCKY_PLAYER) {
    return <SpinHostLuckyPlayerView />;
  }
  if (phase === SW_PHASES.BUILDING_USER_WHEEL) {
    return <SpinHostBuildingUserWheelView />;
  }
  if (phase === SW_PHASES.COUNTDOWN) {
    return <SpinHostCountdownView />;
  }
  // Legacy phases (Users Wheel flow)
  if (phase === SW_PHASES.INTRO) {
    return <SpinHostIntroView gameType={gameType} />;
  }
  if (phase === SW_PHASES.GAME_ABOUT_TO_START) {
    return <SpinHostGameAboutToStartView gameType={gameType} />;
  }
  if (phase === SW_PHASES.FORCE) {
    return <SpinHostForceView gameType={gameType} />;
  }
  if (phase === SW_PHASES.SELECT_HOT_SEAT) {
    return <SpinHostSelectHotSeatView hotSeatName={hotSeatName} />;
  }
  if (phase === SW_PHASES.BUILDING_WHEEL) {
    return <SpinHostBuildingWheelView gameType={gameType} />;
  }
  // WHEEL_READY / SPINNING / RESULT / USER_WHEEL_READY → return null (wheel renders)
  return null;
};

export default renderSpinHostPhaseView;
